/* eslint-disable import/no-unresolved */
import React, { Dispatch, useEffect, useMemo, useRef, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { Redirect, Route, Switch } from 'react-router';
import { useHistory, useLocation } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import { History } from 'history'; // eslint-disable-line
import { isEmpty } from 'lodash';
import { Action } from 'redux';
// eslint-disable-next-line import/no-webpack-loader-syntax
import Worker from 'worker-loader!./worker';

import { PredefinedMilisecondsTypeEnum } from './lib/enums';
import lang from './lib/language';
import { IGNORE_GATEWAY_CONNECTION } from './lib/libSettings';
import { RootState } from './lib/store';
import { getAllMessages } from './lib/store/ams';
import { setOnlineStatus, setWebLoginReady } from './lib/store/app';
import { checkAndRefreshToken } from './lib/store/auth';
import { isOldToken } from './lib/store/auth/helpers';
import {
  applicationStartup,
  clearToken,
  getIndividualExtendedInfo,
  login,
  logout,
  reconnectEventAction,
} from './lib/store/common-actions';
import { getDefaultSelectableTradingPLanFromState } from './lib/store/crm/helpers';
import { CRMCall } from './lib/store/crm/types';
import {
  getCurrentAccount,
  hasValidToken,
  isAlaricBGEnrollStepsCompleted,
  isAuthEnrollPortalStatusInitial,
  isAuthEnrollPortalStatusPending,
  isCRMEnrollReady,
  isLoginPending as storeIsLoginPending,
  isMyPortalTradeEnrollmentStepsCompleted,
  isUserVerifiedStatus,
} from './lib/store/selectors';
import { isMyPortalTrade } from './lib/util/auth/helpers';
import { isDateAfterNow, isDateBeforeNow } from './lib/util/DateTimeHelpers';
import { isCallStatusReady } from './lib/util/error-handling/StatusByCallHelpers';

import TransactionHistory from './containers/Funds/transactions/TransactionHistory/TransactionHistory';
import ChangePasswordConfirmation from './containers/MyProfile/ChangePassword/ChangePasswordConfirmation';
import { redirectToFundPortal, startMockedMode } from './util/helpers';
import { UI_ROUTE_MOCKED, UI_ROUTES } from './util/routes';
import {
  MyAccountDocuments,
  MyAccountNotifications,
  myAccountPersonalDetails,
  Preloader,
  ScrollToTop,
  SearchSymbolsMob,
  TournamentEndTimer,
  TournamentStartTimer,
} from './components';
import { MOCK, setLogConfig } from './configDebug';
import {
  BalancesheetStatement,
  BuyAndSell,
  CashFlowStatement,
  Discover,
  DividendPayersAndHighGrowthCompaniesContainer,
  EarningsCalendarAllItems,
  Enroll,
  EnrollAccountActivated,
  EnrollAuthInfo,
  EnrollCompleted,
  EnrollPersonalInfo,
  EnrollSetupUserName,
  EnrollVerifyEmail,
  EpicMoversInternal,
  Error,
  FiftyTwoWeekHighLow,
  IncomeStatement,
  Login,
  Modify,
  MyAccount,
  MyPosition,
  MyProfile,
  Overview,
  PopularGroupTable,
  SymbolDetails,
  TopGainersAndLosersList,
  TradeHistory,
  TradingViewChart,
  VerificationLanding,
  VerificationPhoneNumber,
  VerificationPhoneOTP,
  VerificationPoA,
  VerificationPoI,
  VerificationTermsConditions,
} from './containers';
import HeaderAndSideBar from './HeaderAndSideBar';
import { useIsMobile } from './hooks';
import useNetwork from './useNetwork';

import styles from './App.module.scss';

type Props = ReduxProps;

const App = (props: Props): JSX.Element => {
  const ref = useRef({ appLoaded: false, path: '' });

  const {
    start,
    isOnline,
    isForceRedirectToEnroll,
    isAuthenticated,
    isAppLoaded,
    isGatewayConnected,
    isEnrollPortalStatusInitial,
    isEnrollPortalStatusPending,
    isMyPortalEnrollCompleted,
    isFundPortalEnrollCompleted,
    isGatewayConnectionTimedOut,
    isTokenValid,
    isPhoneConfirmed,
    isProfileNameCreated,
    isManualToken,
    loginResultCallback,
    setLoginReady,
    isExtendedInfoReady,
    isLoginPending,
    isAppConnected,
    currentAccount,
    extendedInfo,
    isUserVerified,
    getAllDataMessages,
    appLogOut,
    renewToken,
    setNetworkStatus,
    reconnectToGwAndSubscibeSymbols,
    loginRedirect,
    removeTokenFromState,
    getExtendedInfo,
    isOlderToken,
    phoneNumber,
    phoneCode,
    isTournamentEnding,
    isTradingPlanReady,
  } = props;

  const isMobile = useIsMobile();
  const isMyPortalTradeApp = isMyPortalTrade();
  const history = useHistory();
  const location = useLocation();
  const { pathname, search, state } = location;

  const networkStatus = useNetwork();

  useEffect(() => {
    const metaTag = document.querySelector('meta[name="viewport"]');

    if (isMobile && metaTag) {
      metaTag.setAttribute('content', 'width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no');
    }

    return () => {
      if (metaTag) {
        metaTag.setAttribute('content', 'width=device-width, initial-scale=1');
      }
    };
  }, [isMobile]);

  useEffect(() => {
    if (isAuthenticated) {
      getExtendedInfo();
    }
  }, [isAuthenticated, getExtendedInfo]);

  useEffect(() => {
    if (isMyPortalTradeApp && !pathname.includes(UI_ROUTES.tradingView) && isAuthenticated && isOlderToken) {
      // The session check of OIDC lib is not working on the Safari browser properly.
      // That's why we are removing the user data from local and trying to get them from the auth server
      removeTokenFromState();
      loginRedirect(history);
    }
  }, [isAuthenticated, pathname]);// eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (networkStatus && !isOnline) {
      reconnectToGwAndSubscibeSymbols();
    }
    setNetworkStatus(networkStatus);
  }, [isOnline, networkStatus, reconnectToGwAndSubscibeSymbols, setNetworkStatus]);

  const [isRedirected, setRedirected] = useState<boolean>(false);
  const [isLoadedApp, setIsLoadedApp] = useState<boolean>(false);
  const [isRedirectLogin, setIsRedirectLogin] = useState<boolean>(false);

  const searchParams = new URLSearchParams(window.location.search);
  const isDirectLogin = searchParams.get('directLogin');
  if (isDirectLogin && !isRedirectLogin) {
    setIsRedirectLogin(true);
  }

  useEffect(() => {
    if (isRedirectLogin && !isAuthenticated && isAppLoaded) {
      loginRedirect(history);
    }
  }, [isRedirectLogin, isAppLoaded]); // eslint-disable-line react-hooks/exhaustive-deps

  const isErrorPath = location.pathname.includes(UI_ROUTES.error);

  const isEnrollPath = [
    UI_ROUTES.accountActivated,
    UI_ROUTES.enroll,
    UI_ROUTES.enroll + UI_ROUTES.enrollCompleted,
  ].includes(location.pathname) || (isAuthenticated && isExtendedInfoReady && !isPhoneConfirmed)
  || (isAuthenticated && isExtendedInfoReady && !isProfileNameCreated);

  const isFundsPath = [
    UI_ROUTES.funds + UI_ROUTES.withdrawalAmount,
    UI_ROUTES.funds + UI_ROUTES.addYourBankAccount,
    UI_ROUTES.funds + UI_ROUTES.uploadBankStatement,
    UI_ROUTES.funds + UI_ROUTES.enterAmount,
    UI_ROUTES.funds + UI_ROUTES.depositSlip,
    UI_ROUTES.funds + UI_ROUTES.makeYourFirstDeposit,
  ].includes(location.pathname);

  const isVerificationPath = [
    UI_ROUTES.verification,
    UI_ROUTES.verification + UI_ROUTES.verificationPhoneNumber,
    UI_ROUTES.verification + UI_ROUTES.verificationPhoneOTP,
    UI_ROUTES.verification + UI_ROUTES.verificationPoI,
    UI_ROUTES.verification + UI_ROUTES.verificationPoA,
    UI_ROUTES.verification + UI_ROUTES.verificationTermsAndConditions,
  ].includes(location.pathname);

  const isAuthPath = [
    UI_ROUTES.signUp as string,
    UI_ROUTES.authResult as string,
    UI_ROUTES.emailConfirm as string,
    '/',
  ].includes(location.pathname);

  const [isExtraLoader, setIsExtraLoader] = useState<boolean>(true);

  useEffect(() => {
    if (!isLoadedApp) {
      return;
    }

    if (isEnrollPath || isVerificationPath || isAuthPath || !isAuthenticated) {
      setIsExtraLoader(false);
      return;
    }

    setIsExtraLoader(true);
    const timeout = setTimeout(() => {
      setIsExtraLoader(false);
    }, PredefinedMilisecondsTypeEnum.twoSeconds);

    // eslint-disable-next-line consistent-return
    return () => clearTimeout(timeout);
  }, [
    isLoadedApp,
    isEnrollPath,
    isVerificationPath,
    isAuthPath,
    isAuthenticated,
  ]);

  const isBuyOrSellPath = location.pathname.includes(UI_ROUTES.buy) || location.pathname.includes(UI_ROUTES.sell);
  const isModifyPath = location.pathname.includes(UI_ROUTES.modify);
  const isAuthResultPath = location.pathname === UI_ROUTES.authResult;
  const tradeHistoryPath = location.pathname === UI_ROUTES.tradeHistory;
  const tansactionHistoryPath = location.pathname === UI_ROUTES.transactionHistory;
  const searchSymbolPathMob = location.pathname === UI_ROUTES.searchSymbolsMob;
  const isMockedPath = pathname.includes(UI_ROUTE_MOCKED);

  const visibleHeaderAndSidebar = isMobile
    ? (
      !isEnrollPath
      && !isExtraLoader
      && !isVerificationPath
      && !isFundsPath
      && (!isAuthPath)
      && !isBuyOrSellPath
      && !isModifyPath
      && !tradeHistoryPath
      && !tansactionHistoryPath
      && !searchSymbolPathMob
    ) : (
      !isEnrollPath
      && !isExtraLoader
      && !isVerificationPath
      && (!isAuthPath)
    );

  const emailConfirmPath = [
    UI_ROUTES.emailConfirm as string,
    UI_ROUTES.emailResend as string,
  ].includes(pathname);
  const emailConfirmationOrResentWhileLoggedIn = (emailConfirmPath && isTokenValid && !isManualToken);

  /* define the worker */
  const worker = useMemo(() => new Worker(), []);

  /* fetch the worker message */
  useEffect(() => {
    if (window.Worker) {
      worker.onmessage = (e: MessageEvent<string>) => {
        renewToken();
      };
    }
  }, [renewToken, worker]);

  // did mount
  useEffect(() => {
    if (window.Worker) {
      worker.postMessage('start refresh token timer');
    }
    if (ref.current.appLoaded) return;
    ref.current.appLoaded = true;

    if (emailConfirmationOrResentWhileLoggedIn) {
      appLogOut();
      return;
    }
    if (pathname.includes(UI_ROUTE_MOCKED) && !MOCK.ENABLED) {
      startMockedMode();
      history.replace(pathname);
    } else {
      start();
      getAllDataMessages();
    }

    if (search.includes('debug=')) {
      const queryParams = new URLSearchParams(search);
      setLogConfig(queryParams.get('debug'));
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isGatewayConnected && !IGNORE_GATEWAY_CONNECTION) {
      setRedirected(false);
    }
  }, [isGatewayConnected]);

  /**
   * The following two useEffect are for handling auth-result
   * */
  useEffect(() => {
    if (isAppLoaded && isAuthResultPath) {
      loginResultCallback();
    }
  }, [loginResultCallback, isAppLoaded, isAuthResultPath]);

  const hasSession = location.search.includes('session_state=');

  const checkStatusAndRedirect = (): void => {
    if (isExtendedInfoReady && !isAlaricBGEnrollStepsCompleted(extendedInfo?.individual_status)) {
      redirectToFundPortal();
      return;
    }

    if (!isEnrollPortalStatusInitial && !isEnrollPortalStatusPending) {
      setRedirected(true);
      if (isFundPortalEnrollCompleted && !isMyPortalEnrollCompleted) {
        history.replace(UI_ROUTES.enroll);
      } else {
        history.push(UI_ROUTES.overview);
      }
    }
  };

  useEffect(() => {
    if (
      MOCK.ENABLED
      && !pathname.includes(UI_ROUTE_MOCKED)
      && pathname.match(/[/](overview|symbol|discover|payment|reports|trade-history|my-account)/i)
      && pathname !== ref.current.path
    ) {
      ref.current.path = pathname;
      history.replace(pathname + UI_ROUTE_MOCKED);
    }

    if (!ref.current.appLoaded) return;

    // normal login
    if (isAuthenticated && isPhoneConfirmed !== undefined && !isPhoneConfirmed && !isProfileNameCreated) {
      if (!(phoneCode && phoneNumber && isPhoneConfirmed !== undefined) && !isProfileNameCreated) {
        setLoginReady();
        history.push(UI_ROUTES.enrollPhoneVerificaion);
      }
    } else if (isAuthenticated && !isProfileNameCreated && isPhoneConfirmed) {
      setLoginReady();
      history.push(UI_ROUTES.enrollSetupUserName);
    } else if (isAuthResultPath && isTokenValid
      && hasSession && !isRedirected && isPhoneConfirmed && isProfileNameCreated) {
      setLoginReady();
      history.push(UI_ROUTES.overview);
    } else if (!isAuthResultPath && isTokenValid && !isRedirected && isAppLoaded) {
      setRedirected(true);
      setLoginReady();
    }
  }, [// eslint-disable-line react-hooks/exhaustive-deps
    isExtendedInfoReady,
    isPhoneConfirmed,
    isProfileNameCreated,
    isAuthResultPath,
    isTokenValid,
    hasSession,
    setLoginReady,
    history,
    isAppLoaded,
    isAuthenticated,
    isRedirected,
    pathname,
    search,
    isEnrollPortalStatusInitial,
    isEnrollPortalStatusPending,
    isFundPortalEnrollCompleted,
    isMyPortalEnrollCompleted,
    isMyPortalTradeApp,
  ]);

  useEffect(() => {
    if (
      isLoadedApp
      && pathname !== UI_ROUTES.emailConfirm
      && pathname !== UI_ROUTES.emailResend
      && pathname !== UI_ROUTES.signUp
      && pathname !== UI_ROUTES.baseURL
      && (!state && pathname === UI_ROUTES.overview)
    ) {
      if (isMyPortalTradeApp) {
        checkStatusAndRedirect();
        return;
      }

      if (isAuthenticated && isExtendedInfoReady && !phoneCode && !phoneNumber && !isProfileNameCreated) {
        if (!isPhoneConfirmed && !isProfileNameCreated) {
          history.push(UI_ROUTES.enrollPhoneVerificaion);
        } else if (isPhoneConfirmed && !isProfileNameCreated) {
          history.push(UI_ROUTES.enrollSetupUserName);
        } else {
          history.push(UI_ROUTES.overview);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isAuthenticated,
    isExtendedInfoReady,
    isPhoneConfirmed,
    isProfileNameCreated,
    isLoadedApp,
    isEnrollPortalStatusInitial,
    isEnrollPortalStatusPending,
    isFundPortalEnrollCompleted,
    isMyPortalEnrollCompleted,
    isMyPortalTradeApp,
  ]);

  let loaderMessage = '';
  if (
    isAuthResultPath
    || isLoginPending
    || (isTokenValid && !isAppConnected && !isForceRedirectToEnroll && !isManualToken)
    || (isGatewayConnectionTimedOut && !IGNORE_GATEWAY_CONNECTION)
    || emailConfirmationOrResentWhileLoggedIn
    || (MOCK.ENABLED && !isGatewayConnected)
    || (!MOCK.ENABLED && isMockedPath)
    || (!isMyPortalTrade() && !currentAccount && !isForceRedirectToEnroll && isUserVerified)
    || (isAuthenticated && !isExtendedInfoReady)
    || (isMyPortalTradeApp && isEnrollPortalStatusPending)
    || (isAuthenticated && isPhoneConfirmed === undefined)
  ) {
    loaderMessage = isLoginPending ? lang.commonMessageLoading() : lang.commonMessageConnecting();
    if (isMyPortalTradeApp) {
      loaderMessage = '';
    }
    if (isGatewayConnectionTimedOut) loaderMessage = lang.commonMessageConnectionTimedOut();
    if (pathname.match(/tradingView/i)) {
      return (
        <div className={styles.loadingIndicator}>
          <div className={styles.spinner} />
        </div>
      );
    }
    return (
      <div className={styles.loader}>
        <Preloader
          isFullScreen
          text={loaderMessage}
          timeOutSetup={{
            checkIfTimeout: () => !isTokenValid,
            onTimeout: () => {
              appLogOut();
              history.push(UI_ROUTES.signUp);
            },
          }}
          owner="App"
        />
      </div>
    );
  }

  if (!isLoadedApp) {
    setIsLoadedApp(true);
  }

  return (
    <div
      className={[
        !pathname.startsWith(UI_ROUTES.tradingView) && styles.container,
        isEnrollPath && styles.enrollContainer,
        isVerificationPath && styles.enrollContainer,
        isFundsPath && styles.enrollContainer,
        isAuthPath && styles.enrollContainer,
        isErrorPath && styles.enrollContainer,
      ].join(' ')}
    >
      {(isAuthenticated
        && !pathname.startsWith(UI_ROUTES.error)
        && !pathname.startsWith(UI_ROUTES.tradingView)
      ) && (
      <>
        {visibleHeaderAndSidebar && <HeaderAndSideBar />}
        <ToastContainer />
      </>
      )}

      {/* eslint-disable-next-line no-nested-ternary */}
      <div className={isEnrollPath ? undefined : (pathname.startsWith(UI_ROUTES.tradingView) ? styles.whiteBody : (
        isEnrollPath
        || isVerificationPath
        || isFundsPath
        || isAuthPath
        || isErrorPath) ? styles.enrollContent : styles.content)}
      >

        <div
          className={[
            styles.mainContent,
            // eslint-disable-next-line no-nested-ternary
            pathname.startsWith(UI_ROUTES.tradingView) ? null
              : (isMobile ? styles.contentWrapperMobile : styles.contentWrapper),
            isEnrollPath && styles.enrollContentWrapper,
            isVerificationPath && styles.enrollContentWrapper,
            isFundsPath && styles.enrollContentWrapper,
            isAuthPath && styles.enrollContentWrapper,
          ].join(' ')}
        >
          {
            isAuthenticated ? (
              <>
                {isExtendedInfoReady
                  && isPhoneConfirmed
                  && isProfileNameCreated
                  && isTradingPlanReady
                && (
                <div style={{ display: isExtraLoader ? 'none' : 'block' }}>
                  {isTournamentEnding ? <TournamentEndTimer /> : <TournamentStartTimer />}
                </div>
                )}
                {
                  isExtraLoader ? (
                    <div className={styles.loader}>
                      <Preloader
                        isFullScreen
                        text={!isEmpty(loaderMessage) ? loaderMessage : lang.commonMessageConnecting()}
                        owner="App"
                      />
                    </div>
                  ) : (
                    <>
                      <ScrollToTop />
                      <Switch>
                        <Route
                          exact
                          component={TradingViewChart}
                          path={`${UI_ROUTES.tradingView}/:name`}
                        />
                        <Route exact path={UI_ROUTES.error} component={Error} />
                        <Route exact path={UI_ROUTES.myAccount} component={MyAccount} />
                        <Route exact path={UI_ROUTES.myAccount + UI_ROUTE_MOCKED} component={MyAccount} />
                        <Route exact path={UI_ROUTES.myAccountNotifications} component={MyAccountNotifications} />
                        <Route exact path={UI_ROUTES.myAccountDocuments} component={MyAccountDocuments} />
                        <Route exact path={UI_ROUTES.myAccountPersonalDetails} component={myAccountPersonalDetails} />
                        <Route path={UI_ROUTES.overview} component={Overview} />
                        <Route exact path={UI_ROUTES.discover} component={Discover} />
                        <Route exact path={UI_ROUTES.myPosition} component={MyPosition} />
                        <Route exact path={UI_ROUTES.discover + UI_ROUTE_MOCKED} component={Discover} />
                        <Route
                          path={`${UI_ROUTES.symbol}/:name${UI_ROUTES.cashFlowStatement}`}
                          component={CashFlowStatement}
                        />
                        <Route path={`${UI_ROUTES.symbol}/:name${UI_ROUTES.incomeStatement}`} component={IncomeStatement} />
                        <Route
                          path={`${UI_ROUTES.symbol}/:name${UI_ROUTES.balancesheetStatement}`}
                          component={BalancesheetStatement}
                        />
                        <Route path={`${UI_ROUTES.symbol}/:name`} component={SymbolDetails} />
                        <Route path={`${UI_ROUTES.buy}/:name`} component={BuyAndSell} />
                        <Route path={`${UI_ROUTES.sell}/:name`} component={BuyAndSell} />
                        <Route exact path={UI_ROUTES.searchSymbolsMob} component={SearchSymbolsMob} />
                        <Route exact path={UI_ROUTES.enrollPhoneVerificaion} component={VerificationPhoneNumber} />
                        <Route exact path={UI_ROUTES.enrollPhoneVerificaionOtp} component={VerificationPhoneOTP} />
                        <Route exact path={UI_ROUTES.enrollSetupUserName} component={EnrollSetupUserName} />
                        <Route
                          path={UI_ROUTES.discover + UI_ROUTES.topMovers}
                          component={TopGainersAndLosersList}
                        />
                        <Route
                          exact
                          component={Modify}
                          path={UI_ROUTES.modify}
                        />
                        <Route
                          path={UI_ROUTES.discover + UI_ROUTES.popularGroup}
                          component={PopularGroupTable}
                        />
                        <Route
                          path={UI_ROUTES.discover + UI_ROUTES.fiftyTwoWeekHighLow}
                          component={FiftyTwoWeekHighLow}
                        />
                        {/* TODO: Enable New on the Market feature when the backend team fiexes the konown issues */}
                        <Route
                          path={UI_ROUTES.discover + UI_ROUTES.earningsCalendar}
                          component={EarningsCalendarAllItems}
                        />
                        <Route
                          path={UI_ROUTES.discover + UI_ROUTES.epicMoversToday}
                          component={EpicMoversInternal}
                        />
                        <Route path={UI_ROUTES.tradeHistory} component={TradeHistory} />
                        <Route path={UI_ROUTES.transactionHistory} component={TransactionHistory} />
                        <Route path={UI_ROUTES.myProfile} component={MyProfile} />
                        <Route
                          exact
                          component={Enroll}
                          path={UI_ROUTES.enroll}
                        />
                        <Route
                          exact
                          component={EnrollCompleted}
                          path={UI_ROUTES.enroll + UI_ROUTES.enrollCompleted}
                        />
                        <Route
                          exact
                          path={UI_ROUTES.verification}
                          component={VerificationLanding}
                        />
                        <Route
                          exact
                          path={UI_ROUTES.accountActivated}
                          component={EnrollAccountActivated}
                        />
                        <Route
                          exact
                          component={VerificationPoI}
                          path={UI_ROUTES.verification + UI_ROUTES.verificationPoI}
                        />
                        <Route
                          exact
                          component={VerificationPoA}
                          path={UI_ROUTES.verification + UI_ROUTES.verificationPoA}
                        />
                        <Route
                          exact
                          component={VerificationTermsConditions}
                          path={UI_ROUTES.verification + UI_ROUTES.verificationTermsAndConditions}
                        />
                        {/* it is currnetcly not used in HammerWeb  */}
                        <Route
                          exact
                          component={DividendPayersAndHighGrowthCompaniesContainer}
                          path={UI_ROUTES.discover + UI_ROUTES.dividendPayersAndHighGrowthCompaniesPath}
                        />
                        <Route exact component={ChangePasswordConfirmation} path={UI_ROUTES.myAccountChangePassword} />
                        <Redirect to={
                    isMyPortalTradeApp
                      && !isEnrollPortalStatusPending
                      && isFundPortalEnrollCompleted
                      && !isMyPortalEnrollCompleted
                      ? UI_ROUTES.enroll
                      : UI_ROUTES.overview
                  }
                        />
                      </Switch>
                    </>
                  )
                }
              </>
            ) : (
              <Switch>
                <Route exact path="/" component={Login} />
                <Route exact path={UI_ROUTES.error} component={Error} />
                <Route exact path={UI_ROUTES.firstEnroll} component={EnrollPersonalInfo} />
                <Route exact path={UI_ROUTES.secondEnroll} component={EnrollAuthInfo} />
                <Route exact path={UI_ROUTES.emailConfirm} component={EnrollVerifyEmail} />
                <Route exact path={UI_ROUTES.enrollPhoneVerificaion} component={VerificationPhoneNumber} />
                <Route exact path={UI_ROUTES.enrollPhoneVerificaionOtp} component={VerificationPhoneOTP} />
                <Route exact path={UI_ROUTES.enrollSetupUserName} component={EnrollSetupUserName} />
                <Route exact path={UI_ROUTES.signUp} component={Login} />
                <Route exact path={UI_ROUTES.emailResend} component={EnrollVerifyEmail} />
                <Redirect to={UI_ROUTES.signUp} />
              </Switch>
            )
          }
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  isForceRedirectToEnroll: state.auth.isForceRedirectToEnroll,
  isAppLoaded: state.app.loaded,
  isOnline: state.app.isOnline,
  isAppConnected: state.app.connected,
  isLoginPending: storeIsLoginPending(state),
  isOlderToken: isOldToken(state.auth.accessToken),
  isWebLoginReady: state.app.isWebLoginReady,
  isGatewayConnected: state.gateway.connected,
  isGatewayConnectionTimedOut: state.gateway.status === 'timedout',
  isTokenValid: hasValidToken(state),
  isManualToken: state.auth.enroll.isManualToken,
  isAuthenticated: hasValidToken(state) && state.app.loaded,
  extendedInfo: state.crm.individualExtendedInfo,
  isPhoneConfirmed: state.crm.individualExtendedInfo?.phone_number_confirmed,
  isProfileNameCreated: !!(state.crm.individualExtendedInfo?.profile_name),
  currentAccount: getCurrentAccount(state),
  isUserVerified: isUserVerifiedStatus(state.crm.individualExtendedInfo),
  isCRMEnrollReadyStatus: isCRMEnrollReady(state),
  isEnrollPortalStatusInitial: isAuthEnrollPortalStatusInitial(state),
  isEnrollPortalStatusPending: isAuthEnrollPortalStatusPending(state),
  isMyPortalEnrollCompleted: isMyPortalTradeEnrollmentStepsCompleted(state.auth.enrollPortalStatus),
  isFundPortalEnrollCompleted: isAlaricBGEnrollStepsCompleted(state.crm.individualExtendedInfo?.individual_status),
  isExtendedInfoReady: isCallStatusReady(CRMCall.getIndividualExtendedInfo, state.crm.statusByCall),
  isTradingPlanReady: isCallStatusReady(CRMCall.searchTradingPlans, state.crm.statusByCall),
  isTournamentEnding: isDateBeforeNow(getDefaultSelectableTradingPLanFromState(state.crm).plan_purchase_period_end)
    && isDateAfterNow(getDefaultSelectableTradingPLanFromState(state.crm).plans_accounts_deactivation),
  phoneNumber: state.auth.enroll.phoneNumber,
  phoneCode: state.auth.enroll.phoneCode,
});

const mapDispatchToProps = (dispatch: Dispatch<Action>) => ({
  loginRedirect: (history: History) => {
    dispatch(login('login'));
  },
  start: () => dispatch(applicationStartup()),
  appLogOut: () => dispatch(logout()),
  removeTokenFromState: () => dispatch(clearToken()),
  loginResultCallback: () => dispatch(login('callback')),
  setLoginReady: () => dispatch(setWebLoginReady()),
  reconnectToGwAndSubscibeSymbols: () => dispatch(reconnectEventAction('App')),
  getAllDataMessages: () => dispatch(getAllMessages()),
  renewToken: () => dispatch(checkAndRefreshToken()),
  setNetworkStatus: (hasConnection: boolean) => dispatch(setOnlineStatus(hasConnection)),
  getExtendedInfo: (): void => {
    dispatch(getIndividualExtendedInfo());
  },
});

const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default connector(App);
